import { useState, useEffect, useCallback, useMemo } from 'react';
import { api } from '../services/api';

// Global cache to prevent duplicate API calls
let permissionsCache = null;
let permissionsPromise = null;

/**
 * Custom hook for managing user permissions and page access
 */
export const usePermissions = () => {
  const [permissions, setPermissions] = useState([]);
  const [userRole, setUserRole] = useState(null);
  const [permissionsSummary, setPermissionsSummary] = useState(null);
  const [accessiblePages, setAccessiblePages] = useState([]);
  const [navigationMenu, setNavigationMenu] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch user permissions on mount
  useEffect(() => {
    loadUserPermissions();
  }, []);

  const loadUserPermissions = async () => {
    try {
      setLoading(true);
      setError(null);

      // Use cache if available and not expired (5 minutes)
      if (permissionsCache && (Date.now() - permissionsCache.timestamp < 5 * 60 * 1000)) {
        const cached = permissionsCache.data;
        setPermissionsSummary(cached.permissionsResponse.data);
        setPermissions(cached.permissionsResponse.data.all_permissions || []);
        setUserRole(cached.permissionsResponse.data.role);
        setAccessiblePages(cached.accessiblePagesResponse.data.accessible_pages || []);
        setNavigationMenu(cached.navigationResponse.data.navigation);
        setLoading(false);
        return;
      }

      // If already loading, wait for the existing promise
      if (permissionsPromise) {
        const cached = await permissionsPromise;
        setPermissionsSummary(cached.permissionsResponse.data);
        setPermissions(cached.permissionsResponse.data.all_permissions || []);
        setUserRole(cached.permissionsResponse.data.role);
        setAccessiblePages(cached.accessiblePagesResponse.data.accessible_pages || []);
        setNavigationMenu(cached.navigationResponse.data.navigation);
        setLoading(false);
        return;
      }

      // Create new promise for API calls
      permissionsPromise = Promise.all([
        api.get('/my-permissions/'),
        api.get('/accessible-pages/'),
        api.get('/navigation-menu/')
      ]).then(([permissionsResponse, accessiblePagesResponse, navigationResponse]) => ({
        permissionsResponse,
        accessiblePagesResponse,
        navigationResponse
      }));

      const result = await permissionsPromise;
      
      // Cache the result
      permissionsCache = {
        data: result,
        timestamp: Date.now()
      };

      // Set state
      setPermissionsSummary(result.permissionsResponse.data);
      setPermissions(result.permissionsResponse.data.all_permissions || []);
      setUserRole(result.permissionsResponse.data.role);
      setAccessiblePages(result.accessiblePagesResponse.data.accessible_pages || []);
      setNavigationMenu(result.navigationResponse.data.navigation);

      console.log('👤 User Permissions loaded:', result.permissionsResponse.data);
      console.log('📄 Accessible Pages:', result.accessiblePagesResponse.data.accessible_pages);

    } catch (err) {
      console.error('Error loading permissions:', err);
      setError(err.response?.data?.error || 'Failed to load permissions');
    } finally {
      setLoading(false);
      permissionsPromise = null; // Clear promise after completion
    }
  };

  // Check if user has a specific permission
  const hasPermission = useCallback((permissionName) => {
    if (!permissions || permissions.length === 0) return false;
    return permissions.some(perm => perm.name === permissionName);
  }, [permissions]);

  // Check if user has any of the specified permissions
  const hasAnyPermission = useCallback((permissionNames) => {
    if (!Array.isArray(permissionNames)) return hasPermission(permissionNames);
    return permissionNames.some(permName => hasPermission(permName));
  }, [hasPermission]);

  // Check if user has all specified permissions
  const hasAllPermissions = useCallback((permissionNames) => {
    if (!Array.isArray(permissionNames)) return hasPermission(permissionNames);
    return permissionNames.every(permName => hasPermission(permName));
  }, [hasPermission]);

  // Check if user can access a specific page
  const canAccessPage = useCallback((pageName) => {
    return accessiblePages.some(page => page.page === pageName);
  }, [accessiblePages]);

  // Check if user has admin privileges
  const isAdmin = useMemo(() => {
    return userRole === 'ADMIN' || userRole === 'SYSTEM_ADMIN' || hasPermission('admin_access');
  }, [userRole, hasPermission]);

  // Check if user has manager privileges
  const isManager = useMemo(() => {
    return ['ADMIN', 'SYSTEM_ADMIN', 'MEDIA_MANAGER', 'SOCIAL_MANAGER', 'MANAGER'].includes(userRole);
  }, [userRole]);

  // Get permissions by category
  const getPermissionsByCategory = useCallback((category) => {
    return permissions.filter(perm => perm.category === category);
  }, [permissions]);

  // Check specific capabilities
  const capabilities = useMemo(() => {
    return {
      canManageUsers: hasAnyPermission(['manage_users', 'admin_access']),
      canManageCategories: hasAnyPermission(['manage_categories', 'manage_system_settings', 'admin_access']),
      canViewReports: hasPermission('view_reports'),
      canEditCampaigns: hasPermission('edit_campaigns'),
      canDeleteCampaigns: hasPermission('delete_campaigns'),
      canLockAccruals: hasPermission('lock_accruals'),
      canUnlockAccruals: hasPermission('unlock_accruals'),
      canExportData: hasPermission('export_data'),
      canImportData: hasPermission('import_data'),
      canViewDashboard: hasPermission('view_dashboard'),
      canViewFinancials: hasPermission('view_financials'),
      canManageNotifications: hasPermission('manage_notifications'),
    };
  }, [hasPermission, hasAnyPermission]);

  // Refresh permissions
  const refreshPermissions = useCallback(() => {
    // Clear cache to force fresh data
    permissionsCache = null;
    permissionsPromise = null;
    loadUserPermissions();
  }, []);

  // Check multiple pages at once
  const checkBulkPageAccess = useCallback(async (pageNames) => {
    try {
      const response = await api.post('/page-access/bulk-check/', {
        pages: pageNames
      });
      return response.data.page_access;
    } catch (err) {
      console.error('Error checking bulk page access:', err);
      return {};
    }
  }, []);

  return {
    // State
    permissions,
    userRole,
    permissionsSummary,
    accessiblePages,
    navigationMenu,
    loading,
    error,
    
    // Permission checking functions
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    canAccessPage,
    isAdmin,
    isManager,
    
    // Utility functions
    getPermissionsByCategory,
    capabilities,
    refreshPermissions,
    checkBulkPageAccess,
  };
};

/**
 * Component wrapper for conditional rendering based on permissions
 */
export const PermissionWrapper = ({ 
  children, 
  permission = null, 
  anyPermissions = null, 
  allPermissions = null,
  fallback = null,
  requireAdmin = false,
  requireManager = false 
}) => {
  const { hasPermission, hasAnyPermission, hasAllPermissions, isAdmin, isManager, loading } = usePermissions();

  if (loading) {
    return null; // or a loading spinner
  }

  let hasAccess = true;

  // Check specific permission
  if (permission && !hasPermission(permission)) {
    hasAccess = false;
  }

  // Check any of the permissions
  if (anyPermissions && !hasAnyPermission(anyPermissions)) {
    hasAccess = false;
  }

  // Check all permissions
  if (allPermissions && !hasAllPermissions(allPermissions)) {
    hasAccess = false;
  }

  // Check admin requirement
  if (requireAdmin && !isAdmin) {
    hasAccess = false;
  }

  // Check manager requirement
  if (requireManager && !isManager) {
    hasAccess = false;
  }

  return hasAccess ? children : (fallback || null);
};

export default usePermissions; 